// pipelineCardSequences — Vuex module that caches active sequence progress
// by card ID. This enables the PipelineCard badge to read sequence data
// reactively without triggering N+1 API requests on board mount.
//
// Population strategy (MVP):
//   - CardSequencesSection already fetches sequences when a card detail is
//     opened. After fetch, it commits SET_CARD_SEQUENCES here.
//   - PipelineCard reads `activeSequenceByCardId[card.id]` from this store.
//   - Cold-start: badge appears only after the user has opened card details
//     once (or after a background refresh). This is acceptable for Phase 1.2.

import PipelineCardsAPI from 'dashboard/api/pipelineCards';

// Helper: read accountId from the URL. SPA routes guarantee /app/accounts/:id/...
// We avoid coupling to vue-router here so the store stays usable from anywhere.
const currentAccountIdFromRoute = () => {
  if (typeof window === 'undefined') return null;
  const segments = window.location.pathname.split('/');
  // Path layout: ["", "app", "accounts", "<id>", ...]
  const idx = segments.indexOf('accounts');
  if (idx === -1) return null;
  const raw = segments[idx + 1];
  const parsed = Number(raw);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
};

// Janela em que um seed vindo do index NÃO pode sobrescrever uma mutação
// local (pause/resume/cancel feita no detalhe do card): cobre respostas de
// board que estavam em voo quando o usuário agiu.
export const LOCAL_MUTATION_GRACE_MS = 30_000;

const state = {
  // Map of cardId → first active sequence summary (or null)
  // { [cardId]: { id, name, current_step, total_steps, progress_percentage, status } | null }
  activeByCardId: {},
  // cardId → epoch ms da última mutação local (detalhe do card). Seeds do
  // index dentro da janela de graça são descartados para não regredir o badge
  // com uma resposta stale que já estava em voo.
  localMutationAt: {},
  // Track in-flight fetches to avoid duplicate requests
  fetchingCardIds: new Set(),
  // Audit 2026-05-07 — invalidate cache when the user switches between
  // accounts. Without this, cards loaded for account A linger in memory
  // and PipelineCard.vue mounted briefly during a route transition fires
  // /accounts/B/.../cards/<cardA>/sequences → 404 storms (backend protects
  // correctly, but the UX is noisy and confusing).
  lastAccountId: null,
};

const getters = {
  getActiveSequenceForCard: _state => cardId =>
    _state.activeByCardId[cardId] ?? null,
};

const actions = {
  // Called by CardSequencesSection after it fetches sequences so the kanban
  // badge can read the data without its own API call.
  setCardSequences({ commit }, { cardId, sequences }) {
    const active = (sequences || []).find(
      s => s.status === 'active' || s.status === 'paused'
    );
    // Mutação local (detalhe do card) — protege o valor contra seeds stale
    // do index durante a janela de graça.
    commit('SET_LOCAL_MUTATION_AT', { cardId, at: Date.now() });
    commit('SET_CARD_ACTIVE_SEQUENCE', {
      cardId,
      sequence: active
        ? {
            id: active.id,
            name: active.definition?.name || '',
            current_step: active.current_step,
            total_steps: active.total_steps,
            progress_percentage: active.progress_percentage,
            status: active.status,
          }
        : null,
    });
  },

  // Seed the badge cache from cards whose index payload embeds the active
  // sequence (ciclo 3a — kills the per-card GET /cards/:id/sequences N+1).
  // A card WITHOUT the `active_sequence` key (older payload shape) is left
  // untouched so the lazy fetchForCard fallback still applies to it.
  // `active_sequence: null` is seeded as null: it means "no active sequence",
  // which suppresses the fallback fetch — that is the point of the embed.
  seedFromCards({ commit, state: _state }, cards) {
    const now = Date.now();
    (cards || []).forEach(card => {
      if (!card || card.id == null) return;
      if (!('active_sequence' in card)) return;
      // Não regredir uma mutação local recente com uma resposta de index que
      // já estava em voo quando o usuário pausou/retomou/cancelou.
      const mutatedAt = _state.localMutationAt[card.id];
      if (mutatedAt && now - mutatedAt < LOCAL_MUTATION_GRACE_MS) return;
      commit('SET_CARD_ACTIVE_SEQUENCE', {
        cardId: card.id,
        sequence: card.active_sequence,
      });
    });
  },

  // On-demand fetch for a single card — called by PipelineCard on mount
  // if the card is not yet in cache. Guards against duplicate in-flight requests.
  async fetchForCard({ commit, state: _state }, cardId) {
    // Snapshot the accountId at dispatch time. If the route has changed by
    // the time the response arrives, drop the result (defensive — covers
    // browser back/forward + race conditions during route transition).
    const requestAccountId = currentAccountIdFromRoute();

    // Cache invalidation: if user switched accounts, drop everything before
    // honoring any cached value.
    if (
      requestAccountId != null &&
      _state.lastAccountId != null &&
      _state.lastAccountId !== requestAccountId
    ) {
      commit('RESET_FOR_ACCOUNT_SWITCH', requestAccountId);
    } else if (_state.lastAccountId == null && requestAccountId != null) {
      commit('SET_LAST_ACCOUNT_ID', requestAccountId);
    }

    if (
      cardId == null ||
      _state.fetchingCardIds.has(cardId) ||
      cardId in _state.activeByCardId
    ) {
      return;
    }
    commit('SET_FETCHING', { cardId, value: true });
    try {
      const { data } = await PipelineCardsAPI.getCardSequences(cardId);
      // Drop response if route changed mid-flight (defensive — A4).
      const responseAccountId = currentAccountIdFromRoute();
      if (responseAccountId !== requestAccountId) {
        return;
      }
      const sequences = data.data || [];
      const active = sequences.find(
        s => s.status === 'active' || s.status === 'paused'
      );
      commit('SET_CARD_ACTIVE_SEQUENCE', {
        cardId,
        sequence: active
          ? {
              id: active.id,
              name: active.definition?.name || '',
              current_step: active.current_step,
              total_steps: active.total_steps,
              progress_percentage: active.progress_percentage,
              status: active.status,
            }
          : null,
      });
    } catch {
      // Auditoria 2026-08-08 (F22): aqui gravava `sequence: null` no cache, o
      // que é a resposta para "este card não tem sequência" — mas um timeout,
      // 403 ou 500 não sabe disso. Pior: o guard `cardId in activeByCardId`
      // impedia qualquer nova tentativa, então o badge sumia até trocar de
      // conta. Não cachear deixa o próximo acesso ao card tentar de novo.
    } finally {
      commit('SET_FETCHING', { cardId, value: false });
    }
  },

  // Aplica um evento de sequência vindo do WebSocket (PipelineSequenceChannel).
  // O canal e o emissor existiam desde a fase 25, mas nenhum cliente assinava —
  // sequência avançava no servidor e a UI só mostrava depois do F5
  // (fase-57 RT-P2-8).
  //
  // A regra de "qual sequência aparece no badge" (active|paused) fica aqui, no
  // mesmo lugar que setCardSequences e fetchForCard usam, para o realtime não
  // criar uma segunda interpretação do mesmo dado.
  applyRemoteSequenceEvent({ commit }, { sequence } = {}) {
    const cardId = sequence?.pipeline_card_id;
    if (cardId == null) return;

    const isActive =
      sequence.status === 'active' || sequence.status === 'paused';
    const totalSteps = Number(sequence.total_steps) || 0;
    const currentStep = Number(sequence.current_step) || 0;

    commit('SET_LOCAL_MUTATION_AT', { cardId, at: Date.now() });
    commit('SET_CARD_ACTIVE_SEQUENCE', {
      cardId,
      sequence: isActive
        ? {
            id: sequence.id,
            name: sequence.definition_name || '',
            current_step: currentStep,
            total_steps: totalSteps,
            // Mesma fórmula do backend (card_sequences_controller) — o badge do
            // kanban recalcula por conta própria, mas CardSequencesSection lê
            // este campo.
            progress_percentage:
              totalSteps > 0 ? Math.round((currentStep / totalSteps) * 100) : 0,
            status: sequence.status,
          }
        : null,
    });
  },

  // Clear a single card's cache entry (e.g., after completing a step)
  clearForCard({ commit }, cardId) {
    commit('SET_LOCAL_MUTATION_AT', { cardId, at: Date.now() });
    commit('CLEAR_CARD', cardId);
  },

  // Wipe the entire cache. Called by App.vue when accountId changes.
  clearAll({ commit }) {
    commit('RESET_FOR_ACCOUNT_SWITCH', currentAccountIdFromRoute());
  },
};

const mutations = {
  SET_CARD_ACTIVE_SEQUENCE(_state, { cardId, sequence }) {
    _state.activeByCardId = { ..._state.activeByCardId, [cardId]: sequence };
  },
  SET_FETCHING(_state, { cardId, value }) {
    const next = new Set(_state.fetchingCardIds);
    if (value) next.add(cardId);
    else next.delete(cardId);
    _state.fetchingCardIds = next;
  },
  CLEAR_CARD(_state, cardId) {
    const next = { ..._state.activeByCardId };
    delete next[cardId];
    _state.activeByCardId = next;
  },
  RESET_FOR_ACCOUNT_SWITCH(_state, newAccountId) {
    _state.activeByCardId = {};
    _state.localMutationAt = {};
    _state.fetchingCardIds = new Set();
    _state.lastAccountId = newAccountId;
  },
  SET_LOCAL_MUTATION_AT(_state, { cardId, at }) {
    _state.localMutationAt = { ..._state.localMutationAt, [cardId]: at };
  },
  SET_LAST_ACCOUNT_ID(_state, accountId) {
    _state.lastAccountId = accountId;
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
